import { Connection } from 'vscode-languageserver';
import { WebviewPlugin } from '@gitlab-org/webview-plugin';
import { WorkflowAPI } from '@gitlab-lsp/workflow-api';
import { DuoWorkflowMessages, WEBVIEW_ID, WEBVIEW_TITLE } from '../contract';
import {
  DuoWorkflowEventPayload,
  DuoWorkflowEventsType,
  DuoWorkflowCheckpoint,
  DuoWorkflowInfo,
  DuoWorkflowStatus,
  ProjectInfo,
} from '../types';

export interface WorkflowGraphQLService {
  generateWorkflowID(id: string): string;
  getLatestCheckpoint(checkpoints: DuoWorkflowEventPayload[]): DuoWorkflowEventPayload;
  getProjects(ids: string[]): Promise<ProjectInfo[]>;
  getWorkflowById(id: string): Promise<DuoWorkflowEventPayload>;
  getUserWorkflows(): Promise<DuoWorkflowInfo[]>;
  getStatus(duoWorkflowEvent: DuoWorkflowEventPayload): DuoWorkflowStatus;
  parseLangGraphCheckpoint(checkpoint: string): DuoWorkflowCheckpoint;
}

export const workflowPluginFactory = (
  workflowGraphqlAPI: WorkflowGraphQLService,
  workflowApi: WorkflowAPI,
  connection: Connection,
): WebviewPlugin<DuoWorkflowMessages> => {
  return {
    id: WEBVIEW_ID,
    title: WEBVIEW_TITLE,
    setup: ({ webview }) => {
      webview.onInstanceConnected((_webviewInstanceId, messageBus) => {
        messageBus.onNotification('startWorkflow', async ({ goal, image }) => {
          try {
            const workflowId = await workflowApi.runWorkflow(goal, image);
            await workflowApi.subscribeToUpdates(workflowSubscriptionCallback, workflowId);

            messageBus.sendNotification('workflowStarted', workflowId);
          } catch (e) {
            const error = e as Error;
            messageBus.sendNotification('workflowError', error.message);
          }
        });

        messageBus.onNotification('getUserWorkflows', async () => {
          try {
            const workflows: DuoWorkflowInfo[] = await workflowGraphqlAPI.getUserWorkflows();
            messageBus.sendNotification('updateWorkflows', workflows);
            const projectsToFetch = workflows.map((workflow) => workflow.projectId);

            const projects: ProjectInfo[] = await workflowGraphqlAPI.getProjects(projectsToFetch);
            messageBus.sendNotification('updateProjects', projects);
          } catch (e) {
            const error = e as Error;
            messageBus.sendNotification('workflowError', error.message);
          }
        });

        messageBus.onNotification('getWorkflowById', async ({ workflowId }) => {
          const graphqlWorkflowId = `gid://gitlab/Ai::DuoWorkflows::Workflow/${workflowId}`;
          const duoWorkflowEvent = await workflowGraphqlAPI.getWorkflowById(graphqlWorkflowId);

          messageBus.sendNotification('workflowCheckpoint', duoWorkflowEvent);

          const status = workflowGraphqlAPI.getStatus(duoWorkflowEvent);
          messageBus.sendNotification('workflowStatus', status);

          if (status !== 'Completed') {
            await workflowApi.subscribeToUpdates(workflowSubscriptionCallback, workflowId);
          }
        });

        messageBus.onNotification('openUrl', async ({ url }) => {
          await connection.sendNotification('$/gitlab/openUrl', { url });
        });

        async function workflowSubscriptionCallback(
          workflowEvent: DuoWorkflowEventsType,
          status: DuoWorkflowStatus,
        ): Promise<void> {
          const checkpoint = workflowGraphqlAPI.parseLangGraphCheckpoint(workflowEvent.checkpoint);
          messageBus.sendNotification('workflowCheckpoint', {
            ...workflowEvent,
            checkpoint,
          });

          messageBus.sendNotification('workflowStatus', status);
        }
      });
    },
  };
};
